import React, { useCallback } from 'react';
import { SearchCategory } from '@/api/home/types';
import { useOTSTranslation } from '@/i18n/useOTSTranslation';
import { labelMap } from '@/components/home/SearchPopup';
import { cn } from '@/util/cn';

interface ISearchResultsPopUpProps {
  label: string
  data: Array<any>
  dataKey: string
  onSelect: (item: any) => void
}

// Anchored dropdown of search matches. Lives under the search input
// (parent controls visibility). z-50 keeps it above adjacent modules
// (SkiGPT widget, ads) that were occluding it at the legacy z-index.
//
// shadcn Popover would be overkill here — no portal, focus trap, or
// open/close animation is needed; the parent controls open state and
// outside-click dismissal. Tailwind chrome with absolute positioning
// is the right primitive.

const SearchResultsPopUp = ({
  label, data, dataKey, onSelect,
}: ISearchResultsPopUpProps) => {
  const { t } = useOTSTranslation('common');
  const columnLabel = t(labelMap[label as SearchCategory]);

  const onClick = useCallback((e: React.SyntheticEvent) => {
    const element = e.target as HTMLLIElement;
    const { id } = element.dataset;
    if (id) {
      onSelect(data[+id]);
    }
  }, [onSelect, data]);

  if (data.length === 0) return null;

  return (
    <div className="absolute left-0 right-0 top-full z-50 mt-2">
      <div
        className={cn(
          'overflow-hidden rounded-md border border-border bg-white shadow-lg',
          'ring-1 ring-black/5',
        )}
      >
        <header className="border-b border-border bg-muted/40 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {columnLabel}
        </header>
        {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
        <ul
          onClick={onClick}
          className="max-h-72 overflow-auto py-1"
        >
          {data.map((i: any, index) => (
            <li
              key={i[dataKey]}
              data-id={index}
              className={cn(
                'cursor-pointer px-4 py-2 text-sm text-foreground transition-colors',
                'hover:bg-muted',
              )}
            >
              {i[dataKey]}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default SearchResultsPopUp;
